/**
 * Configurateur de Produits Multi-Étapes pour Shopify
 * Inspiré du configurateur Medicis
 * Version: 1.0.0
 */

class ProductConfigurator {
  constructor(config) {
    this.config = config;
    this.currentStep = 0;
    this.configuration = {};
    this.pricing = {
      basePrice: config.basePrice || 0,
      supplements: {},
      totalPrice: config.basePrice || 0
    };
    this.translations = config.translations || {};
    this.container = document.querySelector('.product-configurator');
    this.observers = [];
    
    this.init();
  }

  init() {
    this.loadPersistedData();
    this.renderNavigation();
    this.renderSteps();
    this.bindEvents();
    this.updatePricing();
    this.showCurrentStep();
  }

  // Gestion des données persistantes
  loadPersistedData() {
    const stored = localStorage.getItem(`configurator_${this.config.productId}`);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        this.configuration = data.configuration || {};
        this.currentStep = data.currentStep || 0;
        this.pricing = data.pricing || this.pricing;
      } catch (e) {
        console.warn('Failed to load persisted data:', e);
      }
    }
  }

  persistData() {
    const data = {
      configuration: this.configuration,
      currentStep: this.currentStep,
      pricing: this.pricing,
      timestamp: Date.now()
    };
    localStorage.setItem(`configurator_${this.config.productId}`, JSON.stringify(data));
  }

  // Rendu de la navigation
  renderNavigation() {
    const navigation = this.container.querySelector('.configurator-navigation');
    if (!navigation) return;

    const stepsContainer = navigation.querySelector('.steps-container');
    if (!stepsContainer) return;

    stepsContainer.innerHTML = '';

    this.config.config.steps.forEach((step, index) => {
      const stepElement = document.createElement('div');
      stepElement.className = 'step-item';
      stepElement.dataset.stepIndex = index;
      stepElement.dataset.stepId = step.id;

      const isCompleted = this.isStepCompleted(index);
      const isActive = index === this.currentStep;

      if (isCompleted) stepElement.classList.add('completed');
      if (isActive) stepElement.classList.add('active');

      stepElement.innerHTML = `
        ${index > 0 ? '<div class="step-connector"></div>' : ''}
        <div class="step-indicator">
          <div class="step-number">${isCompleted ? '' : index + 1}</div>
        </div>
        <div class="step-info">
          <div class="step-title">${step.title}</div>
          ${step.description ? `<div class="step-description">${step.description}</div>` : ''}
        </div>
      `;

      stepElement.addEventListener('click', () => {
        if (index < this.currentStep || isCompleted) {
          this.goToStep(index);
        }
      });

      stepsContainer.appendChild(stepElement);
    });

    this.updateProgressBar();
  }

  updateProgressBar() {
    const progressFill = this.container.querySelector('.progress-fill');
    if (!progressFill) return;

    const progress = ((this.currentStep + 1) / this.config.config.steps.length) * 100;
    progressFill.style.width = `${progress}%`;
  }

  // Rendu des étapes
  renderSteps() {
    const stepsContainer = this.container.querySelector('.configurator-steps');
    if (!stepsContainer) return;

    stepsContainer.innerHTML = '';

    this.config.config.steps.forEach((step, index) => {
      const stepElement = document.createElement('div');
      stepElement.className = 'configurator-step';
      stepElement.dataset.stepIndex = index;
      stepElement.dataset.stepId = step.id;

      stepElement.innerHTML = this.renderStepContent(step, index);
      stepsContainer.appendChild(stepElement);
    });
  }

  renderStepContent(step, stepIndex) {
    let content = `
      <div class="step-header">
        <h2>${step.title}</h2>
        ${step.description ? `<div class="step-description">${step.description}</div>` : ''}
      </div>
      <div class="step-content">
    `;

    // Rendu spécifique selon le type d'étape
    if (step.id === 'dragees_selection') {
      content += this.renderDrageesSelection(step);
    } else if (step.id === 'box_customization') {
      content += this.renderBoxCustomization(step);
    } else if (step.id === 'confirmation') {
      content += this.renderConfirmation(step);
    } else {
      content += this.renderGenericStep(step);
    }

    content += `
      </div>
      <div class="step-actions">
        ${stepIndex > 0 ? `<button class="btn btn-secondary" data-action="previous">${this.translations.previousStep || 'Étape précédente'}</button>` : '<div></div>'}
        ${stepIndex < this.config.config.steps.length - 1 ? 
          `<button class="btn btn-primary" data-action="next" ${!this.isStepValid(stepIndex) ? 'disabled' : ''}>${this.translations.nextStep || 'Étape suivante'}</button>` :
          `<button class="btn btn-primary" data-action="add-to-cart" ${!this.isConfigurationComplete() ? 'disabled' : ''}>${this.translations.addToCart || 'Ajouter au panier'}</button>`
        }
      </div>
    `;

    return content;
  }

  renderDrageesSelection(step) {
    let content = '';

    if (step.options.main_dragee) {
      content += `
        <div class="option-group" data-option="main_dragee">
          <h3>${step.options.main_dragee.label || 'Dragée principale'}</h3>
          <div class="product-grid">
      `;

      step.options.main_dragee.products.forEach(product => {
        const isSelected = this.configuration.dragees_selection?.main_dragee?.handle === product.handle;
        content += `
          <div class="product-option ${isSelected ? 'selected' : ''}" 
               data-product-handle="${product.handle}"
               data-option="main_dragee">
            <img class="product-image" 
                 src="/products/${product.handle}/featured_image.jpg" 
                 alt="${product.title || product.handle}"
                 loading="lazy">
            <div class="product-info">
              <h4 class="product-title">${product.title || product.handle}</h4>
              <p class="product-description">${product.description || ''}</p>
              ${product.supplement ? `<div class="product-price supplement">+${this.formatPrice(product.supplement)}</div>` : ''}
              <button class="select-button" data-action="select-product">
                ${isSelected ? 'Sélectionné' : 'Sélectionner'}
              </button>
            </div>
          </div>
        `;
      });

      content += '</div></div>';
    }

    if (step.options.complementary_dragees) {
      content += `
        <div class="option-group" data-option="complementary_dragees">
          <h3>${step.options.complementary_dragees.label || 'Dragées complémentaires (optionnel)'}</h3>
          <div class="product-grid">
      `;

      step.options.complementary_dragees.products.forEach(product => {
        const isSelected = this.configuration.dragees_selection?.complementary_dragees?.some(
          comp => comp.handle === product.handle
        );
        content += `
          <div class="product-option ${isSelected ? 'selected' : ''}" 
               data-product-handle="${product.handle}"
               data-option="complementary_dragees">
            <img class="product-image" 
                 src="/products/${product.handle}/featured_image.jpg" 
                 alt="${product.title || product.handle}"
                 loading="lazy">
            <div class="product-info">
              <h4 class="product-title">${product.title || product.handle}</h4>
              <p class="product-description">${product.description || ''}</p>
              ${product.supplement ? `<div class="product-price supplement">+${this.formatPrice(product.supplement)}</div>` : ''}
              <button class="select-button" data-action="select-product">
                ${isSelected ? 'Sélectionné' : 'Sélectionner'}
              </button>
            </div>
          </div>
        `;
      });

      content += '</div></div>';
    }

    return content;
  }

  renderBoxCustomization(step) {
    let content = '';

    if (step.options.ribbon_color) {
      content += `
        <div class="option-group" data-option="ribbon_color">
          <h3>${step.options.ribbon_color.label || 'Couleur du ruban'}</h3>
          <div class="color-selector">
            <div class="color-palette">
      `;

      step.options.ribbon_color.colors.forEach(color => {
        const isSelected = this.configuration.box_customization?.ribbon_color?.id === color.id;
        content += `
          <div class="color-option ${isSelected ? 'selected' : ''}" 
               data-color-id="${color.id}"
               data-color-name="${color.name}"
               data-color-hex="${color.hex}"
               data-preview-image="${color.preview_image || ''}"
               data-option="ribbon_color">
            <div class="color-swatch" style="background-color: ${color.hex}"></div>
            <span class="color-label">${color.name}</span>
          </div>
        `;
      });

      content += `
            </div>
            <div class="color-preview">
              <div class="preview-container">
                <img class="preview-image" 
                     src="${this.getSelectedColorPreview()}" 
                     alt="Aperçu">
                <div class="color-info">
                  <span class="color-name">${this.getSelectedColorName()}</span>
                  <span class="color-code">${this.getSelectedColorHex()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    if (step.options.custom_label) {
      const isSelected = this.configuration.box_customization?.custom_label === true;
      content += `
        <div class="option-group" data-option="custom_label">
          <h3>${step.options.custom_label.label || 'Options supplémentaires'}</h3>
          <div class="custom-option ${isSelected ? 'selected' : ''}" 
               data-option="custom_label"
               data-supplement="${step.options.custom_label.supplement || 0}">
            <label class="custom-option-label">
              <input type="checkbox" ${isSelected ? 'checked' : ''}>
              <span class="checkmark"></span>
              <span class="option-text">
                ${step.options.custom_label.label}
                ${step.options.custom_label.supplement ? `<span class="supplement">+${this.formatPrice(step.options.custom_label.supplement)}</span>` : ''}
              </span>
            </label>
            ${step.options.custom_label.description ? `<p class="option-description">${step.options.custom_label.description}</p>` : ''}
          </div>
        </div>
      `;
    }

    return content;
  }

  renderConfirmation(step) {
    let content = `
      <div class="configurator-summary">
        <div class="summary-header">
          <h3 class="summary-title">Récapitulatif de votre configuration</h3>
          <div class="summary-price">${this.formatPrice(this.pricing.totalPrice)}</div>
        </div>
        <div class="summary-content">
    `;

    // Résumé des dragées
    if (this.configuration.dragees_selection) {
      content += `
        <div class="summary-section">
          <h4>Dragées sélectionnées</h4>
      `;

      if (this.configuration.dragees_selection.main_dragee) {
        const dragee = this.configuration.dragees_selection.main_dragee;
        content += `
          <div class="summary-item">
            <img class="summary-item-image" 
                 src="/products/${dragee.handle}/featured_image.jpg" 
                 alt="${dragee.title}">
            <div class="summary-item-info">
              <div class="summary-item-name">${dragee.title}</div>
              <div class="summary-item-price">Dragée principale</div>
            </div>
            <button class="modify-button" data-action="modify" data-step="0">Modifier</button>
          </div>
        `;
      }

      if (this.configuration.dragees_selection.complementary_dragees) {
        this.configuration.dragees_selection.complementary_dragees.forEach(dragee => {
          content += `
            <div class="summary-item">
              <img class="summary-item-image" 
                   src="/products/${dragee.handle}/featured_image.jpg" 
                   alt="${dragee.title}">
              <div class="summary-item-info">
                <div class="summary-item-name">${dragee.title}</div>
                <div class="summary-item-price">Dragée complémentaire</div>
              </div>
            </div>
          `;
        });
      }

      content += '</div>';
    }

    // Résumé de la personnalisation
    if (this.configuration.box_customization) {
      content += `
        <div class="summary-section">
          <h4>Personnalisation de la boite</h4>
      `;

      if (this.configuration.box_customization.ribbon_color) {
        const color = this.configuration.box_customization.ribbon_color;
        content += `
          <div class="summary-item">
            <div class="color-swatch" style="background-color: ${color.hex}; width: 50px; height: 50px; border-radius: 6px;"></div>
            <div class="summary-item-info">
              <div class="summary-item-name">Ruban ${color.name}</div>
              <div class="summary-item-price">Inclus</div>
            </div>
            <button class="modify-button" data-action="modify" data-step="1">Modifier</button>
          </div>
        `;
      }

      if (this.configuration.box_customization.custom_label) {
        content += `
          <div class="summary-item">
            <div class="summary-item-info">
              <div class="summary-item-name">Étiquette personnalisée</div>
              <div class="summary-item-price">+${this.formatPrice(15.00)}</div>
            </div>
          </div>
        `;
      }

      content += '</div>';
    }

    content += `
        </div>
        <div class="quantity-cart-section">
          <div class="quantity-selector">
            <span class="quantity-label">Quantité :</span>
            <div class="quantity-controls">
              <button class="quantity-btn" data-action="decrease-quantity">-</button>
              <input type="number" class="quantity-input" value="${this.getQuantity()}" min="1" max="10">
              <button class="quantity-btn" data-action="increase-quantity">+</button>
            </div>
          </div>
          <button class="add-to-cart-btn" data-action="add-to-cart">
            Ajouter au panier - ${this.formatPrice(this.pricing.totalPrice * this.getQuantity())}
          </button>
        </div>
      </div>
    `;

    return content;
  }

  renderGenericStep(step) {
    return `<div class="generic-step">Configuration générique pour ${step.title}</div>`;
  }

  // Gestion des événements
  bindEvents() {
    this.container.addEventListener('click', this.handleClick.bind(this));
    this.container.addEventListener('change', this.handleChange.bind(this));
    
    // Sauvegarde automatique
    setInterval(() => {
      this.persistData();
    }, 5000);
  }

  handleClick(event) {
    const action = event.target.dataset.action;
    if (!action) return;

    event.preventDefault();

    switch (action) {
      case 'select-product':
        this.handleProductSelection(event.target);
        break;
      case 'next':
        this.nextStep();
        break;
      case 'previous':
        this.previousStep();
        break;
      case 'add-to-cart':
        this.addToCart();
        break;
      case 'modify':
        this.goToStep(parseInt(event.target.dataset.step));
        break;
      case 'increase-quantity':
        this.changeQuantity(1);
        break;
      case 'decrease-quantity':
        this.changeQuantity(-1);
        break;
    }
  }

  handleChange(event) {
    if (event.target.classList.contains('quantity-input')) {
      this.setQuantity(parseInt(event.target.value) || 1);
    }
    
    if (event.target.type === 'checkbox' && event.target.closest('[data-option="custom_label"]')) {
      this.handleCustomLabelChange(event.target.checked);
    }
  }

  handleProductSelection(button) {
    const productOption = button.closest('.product-option');
    const optionGroup = button.closest('.option-group');
    const option = optionGroup.dataset.option;
    const stepId = productOption.closest('.configurator-step').dataset.stepId;
    
    const productHandle = productOption.dataset.productHandle;
    const productData = this.getProductData(stepId, option, productHandle);

    if (option === 'main_dragee') {
      // Sélection unique pour la dragée principale
      optionGroup.querySelectorAll('.product-option').forEach(opt => {
        opt.classList.remove('selected');
        opt.querySelector('.select-button').textContent = 'Sélectionner';
      });
      
      productOption.classList.add('selected');
      button.textContent = 'Sélectionné';
      
      this.updateConfiguration(stepId, option, productData);
    } else if (option === 'complementary_dragees') {
      // Sélection multiple pour les dragées complémentaires
      const isSelected = productOption.classList.contains('selected');
      const currentSelections = this.configuration[stepId]?.[option] || [];
      
      if (isSelected) {
        // Désélection
        productOption.classList.remove('selected');
        button.textContent = 'Sélectionner';
        
        const newSelections = currentSelections.filter(item => item.handle !== productHandle);
        this.updateConfiguration(stepId, option, newSelections);
      } else {
        // Sélection (max 2)
        if (currentSelections.length < 2) {
          productOption.classList.add('selected');
          button.textContent = 'Sélectionné';
          
          const newSelections = [...currentSelections, productData];
          this.updateConfiguration(stepId, option, newSelections);
        } else {
          this.showMessage('Vous pouvez sélectionner au maximum 2 dragées complémentaires.', 'warning');
        }
      }
    }

    // Gestion des couleurs
    if (productOption.dataset.option === 'ribbon_color') {
      optionGroup.querySelectorAll('.color-option').forEach(opt => {
        opt.classList.remove('selected');
      });
      
      productOption.classList.add('selected');
      
      const colorData = {
        id: productOption.dataset.colorId,
        name: productOption.dataset.colorName,
        hex: productOption.dataset.colorHex,
        preview_image: productOption.dataset.previewImage
      };
      
      this.updateConfiguration(stepId, option, colorData);
      this.updateColorPreview(colorData);
    }

    this.updateStepValidation();
    this.updatePricing();
  }

  handleCustomLabelChange(checked) {
    const stepId = 'box_customization';
    this.updateConfiguration(stepId, 'custom_label', checked);
    this.updateStepValidation();
    this.updatePricing();
  }

  // Navigation entre étapes
  nextStep() {
    if (this.currentStep < this.config.config.steps.length - 1 && this.isStepValid(this.currentStep)) {
      this.currentStep++;
      this.showCurrentStep();
      this.updateNavigation();
      this.persistData();
    }
  }

  previousStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this.showCurrentStep();
      this.updateNavigation();
      this.persistData();
    }
  }

  goToStep(stepIndex) {
    if (stepIndex >= 0 && stepIndex < this.config.config.steps.length) {
      this.currentStep = stepIndex;
      this.showCurrentStep();
      this.updateNavigation();
      this.persistData();
    }
  }

  showCurrentStep() {
    const steps = this.container.querySelectorAll('.configurator-step');
    steps.forEach((step, index) => {
      step.classList.toggle('active', index === this.currentStep);
    });

    // Scroll vers le haut
    this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateNavigation() {
    this.renderNavigation();
  }

  // Validation
  isStepValid(stepIndex) {
    const step = this.config.config.steps[stepIndex];
    const stepConfig = this.configuration[step.id];

    if (!stepConfig) return false;

    // Validation spécifique par étape
    if (step.id === 'dragees_selection') {
      return stepConfig.main_dragee !== undefined;
    } else if (step.id === 'box_customization') {
      return stepConfig.ribbon_color !== undefined;
    }

    return true;
  }

  isStepCompleted(stepIndex) {
    return stepIndex < this.currentStep || this.isStepValid(stepIndex);
  }

  isConfigurationComplete() {
    return this.config.config.steps.every((step, index) => this.isStepValid(index));
  }

  updateStepValidation() {
    const currentStepElement = this.container.querySelector(`.configurator-step[data-step-index="${this.currentStep}"]`);
    if (!currentStepElement) return;

    const nextButton = currentStepElement.querySelector('[data-action="next"]');
    const addToCartButton = currentStepElement.querySelector('[data-action="add-to-cart"]');

    if (nextButton) {
      nextButton.disabled = !this.isStepValid(this.currentStep);
    }

    if (addToCartButton) {
      addToCartButton.disabled = !this.isConfigurationComplete();
    }
  }

  // Gestion de la configuration
  updateConfiguration(stepId, option, value) {
    if (!this.configuration[stepId]) {
      this.configuration[stepId] = {};
    }
    this.configuration[stepId][option] = value;
    
    this.notifyObservers('configuration_updated', { stepId, option, value });
  }

  getProductData(stepId, option, productHandle) {
    const step = this.config.config.steps.find(s => s.id === stepId);
    if (!step || !step.options[option]) return null;

    const product = step.options[option].products.find(p => p.handle === productHandle);
    return product ? { ...product } : { handle: productHandle };
  }

  // Gestion du pricing
  updatePricing() {
    let totalPrice = this.pricing.basePrice;
    const supplements = {};

    Object.entries(this.configuration).forEach(([stepId, stepConfig]) => {
      Object.entries(stepConfig).forEach(([optionKey, value]) => {
        const supplement = this.calculateSupplement(stepId, optionKey, value);
        if (supplement > 0) {
          const key = `${stepId}_${optionKey}`;
          supplements[key] = supplement;
          totalPrice += supplement;
        }
      });
    });

    this.pricing.supplements = supplements;
    this.pricing.totalPrice = totalPrice;

    this.updatePriceDisplay();
    this.notifyObservers('pricing_updated', this.pricing);
  }

  calculateSupplement(stepId, optionKey, value) {
    const step = this.config.config.steps.find(s => s.id === stepId);
    if (!step || !step.options[optionKey]) return 0;

    const option = step.options[optionKey];

    if (optionKey === 'main_dragee' && value && value.supplement) {
      return value.supplement;
    }

    if (optionKey === 'complementary_dragees' && Array.isArray(value)) {
      return value.reduce((sum, item) => sum + (item.supplement || 0), 0);
    }

    if (optionKey === 'custom_label' && value === true && option.supplement) {
      return option.supplement;
    }

    return 0;
  }

  updatePriceDisplay() {
    const priceElements = this.container.querySelectorAll('.current-price');
    priceElements.forEach(element => {
      element.textContent = this.formatPrice(this.pricing.totalPrice);
    });

    const addToCartButton = this.container.querySelector('.add-to-cart-btn');
    if (addToCartButton) {
      const quantity = this.getQuantity();
      addToCartButton.textContent = `Ajouter au panier - ${this.formatPrice(this.pricing.totalPrice * quantity)}`;
    }
  }

  // Gestion de la quantité
  getQuantity() {
    return this.configuration.quantity || 1;
  }

  setQuantity(quantity) {
    quantity = Math.max(1, Math.min(10, quantity));
    this.configuration.quantity = quantity;
    
    const quantityInput = this.container.querySelector('.quantity-input');
    if (quantityInput) {
      quantityInput.value = quantity;
    }
    
    this.updatePriceDisplay();
    this.persistData();
  }

  changeQuantity(delta) {
    this.setQuantity(this.getQuantity() + delta);
  }

  // Aperçu des couleurs
  getSelectedColorPreview() {
    const color = this.configuration.box_customization?.ribbon_color;
    return color?.preview_image || '/assets/preview-default.jpg';
  }

  getSelectedColorName() {
    const color = this.configuration.box_customization?.ribbon_color;
    return color?.name || 'Aucune couleur sélectionnée';
  }

  getSelectedColorHex() {
    const color = this.configuration.box_customization?.ribbon_color;
    return color?.hex || '';
  }

  updateColorPreview(colorData) {
    const preview = this.container.querySelector('.color-preview');
    if (!preview) return;

    const previewImage = preview.querySelector('.preview-image');
    const colorName = preview.querySelector('.color-name');
    const colorCode = preview.querySelector('.color-code');

    if (previewImage && colorData.preview_image) {
      previewImage.src = colorData.preview_image;
    }

    if (colorName) colorName.textContent = colorData.name;
    if (colorCode) colorCode.textContent = colorData.hex;
  }

  // Ajout au panier
  async addToCart() {
    if (!this.isConfigurationComplete()) {
      this.showMessage('Veuillez compléter toutes les étapes avant d\'ajouter au panier.', 'error');
      return;
    }

    const cartData = this.buildCartData();
    
    try {
      this.showLoading(true);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });

      if (response.ok) {
        const result = await response.json();
        this.showMessage('Produit ajouté au panier avec succès !', 'success');
        this.clearConfiguration();
        
        // Redirection vers le panier ou fermeture du configurateur
        setTimeout(() => {
          window.location.href = '/cart';
        }, 2000);
      } else {
        throw new Error('Erreur lors de l\'ajout au panier');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showMessage('Erreur lors de l\'ajout au panier. Veuillez réessayer.', 'error');
    } finally {
      this.showLoading(false);
    }
  }

  buildCartData() {
    const items = [];
    const properties = {};

    // Construction des propriétés du produit principal
    const mainProduct = {
      id: this.config.productId,
      quantity: this.getQuantity(),
      properties: {
        '_configurator_data': JSON.stringify(this.configuration),
        '_configurator_pricing': JSON.stringify(this.pricing)
      }
    };

    // Ajout des détails de configuration
    Object.entries(this.configuration).forEach(([stepId, stepConfig]) => {
      Object.entries(stepConfig).forEach(([optionKey, value]) => {
        if (optionKey !== 'quantity') {
          mainProduct.properties[`_${stepId}_${optionKey}`] = JSON.stringify(value);
        }
      });
    });

    items.push(mainProduct);

    return { items };
  }

  // Utilitaires
  formatPrice(price) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: this.config.currency || 'EUR'
    }).format(price);
  }

  showMessage(message, type = 'info') {
    const messageElement = document.createElement('div');
    messageElement.className = `configurator-message ${type}`;
    messageElement.textContent = message;

    this.container.insertBefore(messageElement, this.container.firstChild);

    setTimeout(() => {
      messageElement.remove();
    }, 5000);
  }

  showLoading(show) {
    this.container.classList.toggle('loading', show);
  }

  clearConfiguration() {
    this.configuration = {};
    this.currentStep = 0;
    localStorage.removeItem(`configurator_${this.config.productId}`);
  }

  // Pattern Observer
  subscribe(observer) {
    this.observers.push(observer);
  }

  unsubscribe(observer) {
    this.observers = this.observers.filter(obs => obs !== observer);
  }

  notifyObservers(event, data) {
    this.observers.forEach(observer => {
      if (typeof observer === 'function') {
        observer(event, data);
      }
    });
  }

  // API publique
  getConfiguration() {
    return { ...this.configuration };
  }

  getPricing() {
    return { ...this.pricing };
  }

  getCurrentStep() {
    return this.currentStep;
  }
}

// Initialisation automatique
document.addEventListener('DOMContentLoaded', function() {
  const configData = document.getElementById('configurator-data');
  if (configData) {
    try {
      const config = JSON.parse(configData.textContent);
      window.productConfigurator = new ProductConfigurator(config);
    } catch (error) {
      console.error('Failed to initialize configurator:', error);
    }
  }
});

// Export pour utilisation en module
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductConfigurator;
}

